'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import type { TagGroup } from '@/lib/data/software';

interface FilterSidebarProps {
  tagGroups: TagGroup[];
  selectedTags: string[];
  basePath?: string;
}

// Tag color mappings
const tagColorClasses: Record<string, string> = {
  red: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  orange: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  yellow: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  green: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  cyan: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800',
  sky: 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800',
  blue: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  purple: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  pink: 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300 border-pink-200 dark:border-pink-800',
  gray: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700',
};

const selectedColorClasses: Record<string, string> = {
  red: 'bg-red-600 text-white border-red-600',
  orange: 'bg-orange-600 text-white border-orange-600',
  yellow: 'bg-yellow-600 text-white border-yellow-600',
  green: 'bg-green-700 text-white border-green-600',
  cyan: 'bg-cyan-600 text-white border-cyan-600',
  sky: 'bg-sky-600 text-white border-sky-600',
  blue: 'bg-blue-600 text-white border-blue-600',
  purple: 'bg-purple-600 text-white border-purple-600',
  pink: 'bg-pink-600 text-white border-pink-600',
  gray: 'bg-gray-600 text-white border-gray-600',
};

export default function FilterSidebar({ tagGroups, selectedTags, basePath = '/software/browse' }: FilterSidebarProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(tagGroups.map(g => g.category)));

  const toggleGroup = (category: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleTag = (tagSlug: string) => {
    const currentTags = new Set(selectedTags);
    if (currentTags.has(tagSlug)) {
      currentTags.delete(tagSlug);
    } else {
      currentTags.add(tagSlug);
    }

    const params = new URLSearchParams(searchParams.toString());
    if (currentTags.size > 0) {
      params.set('tags', Array.from(currentTags).join(','));
    } else {
      params.delete('tags');
    }

    // Preserve sort param
    const sort = searchParams.get('sort');
    if (sort) params.set('sort', sort);

    router.push(`${basePath}?${params.toString()}`);
  };

  const clearFilters = () => {
    const params = new URLSearchParams();
    const sort = searchParams.get('sort');
    if (sort) params.set('sort', sort);
    router.push(`${basePath}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Filters</h3>
        </div>
        {selectedTags.length > 0 && (
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Active filters:</p>
          <div className="flex flex-wrap gap-1.5">
            {selectedTags.map((tagSlug) => {
              const tag = tagGroups
                .flatMap(g => g.tags)
                .find(t => t.slug === tagSlug);
              if (!tag) return null;
              return (
                <button
                  key={tagSlug}
                  onClick={() => toggleTag(tagSlug)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${selectedColorClasses[tag.color] || selectedColorClasses.gray}`}
                >
                  {tag.name}
                  <X className="w-3 h-3" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tag Groups */}
      <div className="space-y-3">
        {tagGroups.map((group) => (
          <div key={group.category}>
            <button
              onClick={() => toggleGroup(group.category)}
              className="flex items-center justify-between w-full text-left py-1"
            >
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{group.category}</span>
              {expandedGroups.has(group.category) ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>
            {expandedGroups.has(group.category) && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {group.tags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.slug);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.slug)}
                      className={`px-2 py-0.5 text-xs font-medium rounded-full border transition-colors ${
                        isSelected
                          ? selectedColorClasses[tag.color] || selectedColorClasses.gray
                          : tagColorClasses[tag.color] || tagColorClasses.gray
                      }`}
                    >
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Browse All Tags Link */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Link
          href="/software/tags"
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          Browse all tags &rarr;
        </Link>
      </div>
    </div>
  );
}
